// inputValidator.js — config-driven real-time input restriction engine.
// Driven entirely by field.validator in formGroupConfig — no hardcoding.
// Used by FieldControl in AddFormV1 and EditFormV1.

export const INPUT_VALIDATOR_TYPES = [
  { label: 'Only Numbers',                         value: 'onlyNumber' },
  { label: 'Only Letters',                          value: 'onlyLetter' },
  { label: 'Only Letters & Spaces',                 value: 'onlyLettersAndSpace' },
  { label: 'Only Alphanumeric',                     value: 'onlyAlphanumeric' },
  { label: 'Letters, Numbers & Hyphen',             value: 'onlyLettersNumberAndHyphen' },
  { label: 'Letters, Numbers, Hyphen & Dot',        value: 'onlyLettersNumberHyphenAndDot' },
  { label: 'Contact Number  (digits, (), -)',        value: 'contactNumber' },
  { label: 'Email format',                          value: 'email' },
  { label: 'Job Title (letters, nums, symbols)',    value: 'jobTitle' },
  { label: 'MSP / Ref ID (alphanumeric only)',      value: 'mspRefId' },
  { label: 'Location (letters, nums, , . -)',       value: 'locationValidation' },
  { label: 'Decimal / Range  (number + dot)',       value: 'decimalRange' },
  { label: 'Budget (numbers, must be > 0)',         value: 'budgetValidation' },
  { label: 'Experience (numbers only)',             value: 'experience' },
  { label: 'Numeric — 2 digits max',               value: 'numericTwoDigits' },
  { label: 'Job Description (character count)',     value: 'jobDescription' },
  { label: 'Website URL',                           value: 'websiteUrl' },
];

// onKeyPress allowlist patterns — used to block invalid chars before they appear.
// null means no per-key blocking for that type.
const KEY_PATTERNS = {
  onlyNumber:                 /^[0-9]$/,
  experience:                 /^[0-9]$/,
  numericTwoDigits:           /^[0-9]$/,
  budgetValidation:           /^[0-9]$/,
  onlyLetter:                 /^[a-zA-Z\s]$/,
  onlyLettersAndSpace:        /^[a-zA-Z\s]$/,
  onlyAlphanumeric:           /^[a-zA-Z0-9]$/,
  mspRefId:                   /^[a-zA-Z0-9]$/,
  onlyLettersNumberAndHyphen: /^[a-zA-Z0-9\s-]$/,
  locationValidation:         /^[a-zA-Z0-9\s\-.,]$/,
  jobTitle:                   /^[a-zA-Z0-9\s\-.,/&+()*'"#@]$/,
  decimalRange:               /^[0-9.]$/,
  contactNumber:              /^[0-9()\-+\s]$/,
};

export function getKeyPattern(type) {
  return KEY_PATTERNS[type] ?? null;
}

// applyInputValidator — clean a raw input value according to the validator config.
// Returns { cleaned: string, error: string|null }.
// eventType 'blur' triggers trim; 'change' only strips leading whitespace.
export function applyInputValidator(rawValue, config = {}, eventType = 'change') {
  if (!config?.type || rawValue === undefined || rawValue === null) {
    return { cleaned: rawValue ?? '', error: null };
  }

  const value = String(rawValue);
  const { type, maxLength, maxChars } = config;
  const title = config.title || 'Field';
  let cleaned;
  let error = null;

  const trimStart = (s) => (eventType === 'blur' ? s.trim() : s.replace(/^\s+/, ''));

  switch (type) {
    case 'onlyNumber':
    case 'experience':
    case 'numericTwoDigits': {
      const limit = Number(maxLength ?? (type === 'numericTwoDigits' ? 2 : 10));
      cleaned = value.replace(/[^0-9]/g, '');
      if (value !== cleaned) error = `${title} allows only numbers.`;
      if (cleaned.length > limit) {
        error = `${title} cannot exceed ${limit} characters.`;
        cleaned = cleaned.slice(0, limit);
      }
      break;
    }

    case 'onlyLetter': {
      const limit = Number(maxLength ?? 50);
      cleaned = value.replace(/[^a-zA-Z]/g, '');
      if (value !== cleaned) error = `${title} allows only letters.`;
      if (cleaned.length > limit) { error = `${title} cannot exceed ${limit} characters.`; cleaned = cleaned.slice(0, limit); }
      break;
    }

    case 'onlyLettersAndSpace': {
      const limit = Number(maxLength ?? 55);
      cleaned = trimStart(value.replace(/[^a-zA-Z\s]/g, ''));
      if (value.replace(/^\s+/, '') !== cleaned) error = `${title} allows only letters and spaces.`;
      if (cleaned.length > limit) { error = `${title} cannot exceed ${limit} characters.`; cleaned = cleaned.slice(0, limit); }
      break;
    }

    case 'onlyAlphanumeric':
    case 'mspRefId': {
      const limit = Number(maxLength ?? 50);
      cleaned = trimStart(value.replace(/[^a-zA-Z0-9]/g, ''));
      if (value.replace(/^\s+/, '') !== cleaned) error = `${title} allows only letters and numbers.`;
      if (cleaned.length > limit) { error = `${title} cannot exceed ${limit} characters.`; cleaned = cleaned.slice(0, limit); }
      break;
    }

    case 'onlyLettersNumberAndHyphen': {
      const limit = Number(maxLength ?? 55);
      cleaned = trimStart(value.replace(/[^a-zA-Z0-9\s-]/g, ''));
      if (value.replace(/^\s+/, '') !== cleaned) error = `${title} allows only letters, numbers and hyphens.`;
      if (cleaned.length > limit) { error = `${title} cannot exceed ${limit} characters.`; cleaned = cleaned.slice(0, limit); }
      break;
    }

    case 'onlyLettersNumberHyphenAndDot': {
      const limit = Number(maxLength ?? 100);
      cleaned = value.replace(/[^a-zA-Z0-9\-./#+[\]{}()\s]/g, '').slice(0, limit);
      if (value !== cleaned) error = `${title} allows only letters, numbers, hyphens and dots.`;
      break;
    }

    case 'contactNumber': {
      const limit = Number(maxLength ?? 15);
      cleaned = value.replace(/[^0-9()\-+\s]/g, '').slice(0, limit);
      const re = /^(\+?[0-9]{1,3}[- ]?)?(\(?\d{1,4}\)?[- ]?)?[\d\-\s]{3,15}$/;
      if (value !== cleaned) error = `${title} allows only digits, parentheses () and hyphens.`;
      else if (cleaned && !re.test(cleaned)) error = `${title} is not a valid phone number format.`;
      break;
    }

    case 'locationValidation': {
      const limit = Number(maxLength ?? 100);
      cleaned = trimStart(value.replace(/[^a-zA-Z0-9\s\-.,]/g, ''));
      if (value.replace(/^\s+/, '') !== cleaned) error = `${title} allows only letters, numbers, spaces, commas, dots and hyphens.`;
      if (cleaned.length > limit) { error = `${title} cannot exceed ${limit} characters.`; cleaned = cleaned.slice(0, limit); }
      break;
    }

    case 'jobTitle': {
      const limit = Number(maxLength ?? 80);
      cleaned = trimStart(value.replace(/[^a-zA-Z0-9\s\-/&.,()+#@'"*]/g, ''));
      if (value.replace(/^\s+/, '') !== cleaned) error = `${title} allows only letters, numbers, spaces and common symbols.`;
      if (cleaned.length > limit) { error = `${title} cannot exceed ${limit} characters.`; cleaned = cleaned.slice(0, limit); }
      break;
    }

    case 'decimalRange': {
      let c = value.replace(/[^0-9.]/g, '');
      const dotCount = (c.match(/\./g) || []).length;
      if (dotCount > 1) {
        const di = c.indexOf('.');
        c = c.slice(0, di + 1) + c.slice(di + 1).replace(/\./g, '');
        error = `${title} can have only one decimal point.`;
      }
      const digits = c.replace(/\./g, '');
      const limit = Number(maxLength ?? 10);
      if (digits.length > limit) {
        c = c.slice(0, limit + (c.includes('.') ? 1 : 0));
        error = `${title} cannot exceed ${limit} digits.`;
      }
      cleaned = c;
      if (!error && value !== cleaned) error = `${title} allows only numbers and one dot.`;
      break;
    }

    case 'budgetValidation': {
      const limit = Number(maxLength ?? 10);
      cleaned = value.replace(/[^0-9]/g, '').slice(0, limit);
      const num = Number(cleaned);
      if (value !== cleaned) error = `${title} allows only numbers.`;
      else if (cleaned && num <= 0) error = `${title} must be greater than 0.`;
      break;
    }

    case 'jobDescription': {
      const limit = Number(maxChars ?? maxLength ?? 1500);
      const plain = value
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (plain.length > limit) error = `${title} cannot exceed ${limit} characters.`;
      cleaned = value; // never truncate HTML content
      break;
    }

    case 'email': {
      cleaned = eventType === 'blur' ? value.trim() : value;
      const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (cleaned && !emailRe.test(cleaned)) error = `${title} must be a valid email address.`;
      break;
    }

    case 'websiteUrl': {
      cleaned = eventType === 'blur' ? value.trim() : value;
      const urlRe = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
      if (cleaned && !urlRe.test(cleaned)) error = `${title} must be a valid URL.`;
      break;
    }

    default:
      cleaned = value;
  }

  return { cleaned, error };
}
